import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  Stack,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  Box,
} from '@mui/material';
import React, { useState } from 'react';
import {
  PortalRole,
  useUpdateUserMutation,
  useListUsersQuery,
} from 'src/backend';
import LoadingButton from 'src/components/LoadingButton';

export type User = {
  id: string;
  name: string;
  email: string;
  role: PortalRole;
  isDeleted: boolean;
  createdAt: string;
  createdBy: string;
  lastModifiedAt: string;
  lastModifiedBy: string;
  customerIds: string[];
};

export type UpdateUserRequest = {
  name?: string;
  role?: string;
  customerIds?: string[];
};

export type Customer = {
  id: string;
  name: string;
};

interface EditUserDialogProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onUserUpdated: () => void;
  customers: Customer[];
}

function EditUserDialog({
  open,
  user,
  onClose,
  onUserUpdated,
  customers,
}: EditUserDialogProps) {
  const [editUser, setEditUser] = useState<{
    id: string;
    name: string;
    role: PortalRole;
    customerIds: string[];
  } | null>(null);

  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation();
  const { data } = useListUsersQuery({
    filter: undefined,
    page: 0,
    take: 1000, // Large number to get all users for email lookup
  });

  const getPortalRoleOptions = () => [
    { value: PortalRole.User, label: 'User' },
    { value: PortalRole.Admin, label: 'Admin' },
  ];

  // Initialize editUser when dialog opens with a user
  React.useEffect(() => {
    if (open && user) {
      setEditUser({
        id: user.id,
        name: user.name,
        role:
          typeof user.role === 'string'
            ? PortalRole[user.role as keyof typeof PortalRole]
            : user.role,
        customerIds: user.customerIds || [],
      });
    } else if (!open) {
      setEditUser(null);
    }
  }, [open, user]);

  const handleUpdateUser = () => {
    if (editUser) {
      const request: UpdateUserRequest = {
        name: editUser.name,
        role: PortalRole[editUser.role],
        // Only include customerIds for non-Admin users
        customerIds:
          editUser.role === PortalRole.Admin ? undefined : editUser.customerIds,
      };

      updateUser({ userId: editUser.id, request })
        .unwrap()
        .then(() => {
          onUserUpdated();
          onClose();
        });
    }
  };

  const handleClose = () => {
    onClose();
    setEditUser(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ padding: '2rem', fontSize: '2.5rem' }}>
        Edit User
      </DialogTitle>
      <DialogContent sx={{ padding: '2rem' }}>
        <Stack spacing={3}>
          <TextField
            label="Name"
            value={editUser?.name || ''}
            onChange={(e) =>
              setEditUser((prev) =>
                prev ? { ...prev, name: e.target.value } : null,
              )
            }
            fullWidth
            required
          />
          <TextField
            label="Email"
            type="email"
            value={
              editUser?.id
                ? data?.data.find((u) => u.id === editUser.id)?.email || ''
                : ''
            }
            fullWidth
            disabled
            helperText="Email cannot be changed"
          />
          <FormControl fullWidth>
            <TextField
              select
              label="Role"
              value={editUser?.role || PortalRole.User}
              onChange={(e) =>
                setEditUser((prev) =>
                  prev
                    ? {
                        ...prev,
                        role: Number(e.target.value) as PortalRole,
                        // Clear customerIds when Admin is selected
                        customerIds:
                          Number(e.target.value) === PortalRole.Admin
                            ? []
                            : prev.customerIds,
                      }
                    : null,
                )
              }
            >
              {getPortalRoleOptions().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="edit-customers-label">Allowed customers</InputLabel>
            <Select
              labelId="edit-customers-label"
              multiple
              value={
                (editUser?.role === PortalRole.Admin
                  ? []
                  : editUser?.customerIds) || []
              }
              disabled={editUser?.role === PortalRole.Admin}
              onChange={(e) =>
                setEditUser((prev) =>
                  prev
                    ? {
                        ...prev,
                        customerIds:
                          typeof e.target.value === 'string'
                            ? e.target.value.split(',')
                            : e.target.value,
                      }
                    : null,
                )
              }
              input={<OutlinedInput label="Allowed customers" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={
                        customers.find((c) => c.id === value)?.name || value
                      }
                      size="small"
                    />
                  ))}
                </Box>
              )}
            >
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.name}
                </MenuItem>
              ))}
            </Select>
            {editUser?.role === PortalRole.Admin && (
              <Box
                sx={{
                  mt: 1,
                  color: 'text.secondary',
                  fontSize: '1.2rem',
                }}
              >
                Admin users have access to all customers automatically
              </Box>
            )}
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          loading={isUpdatingUser}
          variant="contained"
          onClick={handleUpdateUser}
          disabled={!editUser?.name}
        >
          Update User
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditUserDialog;
